import Pluralize from 'pluralize';
import React from 'react';

import PreviewOrLaunchModal from '../../shared/preview_or_launch_modal';
import { DataTable, DataTableChip, EXTRA_SMALL_ICON_BASE_SRC } from '../../../../Shared';

const headers = [
  {
    name: 'Tool',
    attribute: 'tool',
    width: '42px',
    isSortable: false,
    noTooltip: true
  },
  {
    name: 'Activity',
    attribute: 'activity',
    width: '754px',
    isSortable: false,
    noTooltip: true,
    rowSectionClassName: 'lesson-activity',
    headerClassName: 'lesson-activity-header'
  },
  {
    name: 'Launch Lesson',
    attribute: 'launchLesson',
    width: '150px',
    isSortable: false,
    noTooltip: true,
    rowSectionClassName: 'launch-lesson',
    headerClassName: 'launch-lesson-header'
  },
  {
    name: 'Customize',
    attribute: 'customize',
    width: '85px',
    isSortable: false,
    noTooltip: true,
    rowSectionClassName: 'center-content',
    headerClassName: 'center-content'
  },
  {
    name: 'Download',
    attribute: 'download',
    width: '85px',
    isSortable: false,
    noTooltip: true,
    rowSectionClassName: 'center-content',
    headerClassName: 'center-content'
  },
]

const sharedButtonClassName = "quill-button contained extra-small green focus-on-light"

export const Unit = ({ data }) => {
  const {
    classroomActivities,
    classroom_activities,
    classrooms,
    name,
    num_students_assigned,
    studentCount,
    unit,
    unitId,
    unitName
  } = data
  const [lessonActivityId, setLessonActivityId] = React.useState<number>(null)
  const [showModal, setShowModal] = React.useState<boolean>(false)

  function assignedToText () {
    // ensure classrooms is always an array as sometimes it is passed a set
    // and we need to do a number of things with it that are better with an array
    const classroomsArray = Array.isArray(classrooms) ? classrooms : Array.from(classrooms);
    const studentCountData = num_students_assigned || studentCount;
    return (
      <div className="assigned-to">
        <p>Assigned to</p>
        <span className="bolded-value">{`${studentCountData} ${Pluralize('Student', studentCountData)}`}</span>
        <p>in</p>
        <span className="bolded-value">{`${classroomsArray.length} ${Pluralize('class', classroomsArray.length)} (${classroomsArray.join(', ')})`}</span>
      </div>
    )
  };

  function lockedInfo () {
    const firstClassroomActivity = classroomActivities.values().next().value;
    const ownedByCurrentUser = firstClassroomActivity?.ownedByCurrentUser;
    if(ownedByCurrentUser) { return }
    return(
      <span className="locked-info">
        <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/locked.svg`} />
        Created By {firstClassroomActivity.ownerName}
      </span>
    );
  };

  function handleDataTableChipClick(e, id) {
    setLessonActivityId(id)
    handleToggleModal()
  }

  function handleToggleModal() {
    setShowModal(!showModal)
  }

  function getClassroomActivities() {
    if (Object.keys(classroomActivities)) {
      return classroomActivities
    }
    if (Object.keys(classroom_activities)) {
      return classroom_activities
    }
  }

  function getActivityId(lessonActivity) {
    const { activityId, activityUid, activity } = lessonActivity
    return activityId || activityUid || activity?.uid
  }

  function getClassroomUnitId(lessonActivity) {
    const { cuId, classroom_unit_id } = lessonActivity
    return cuId || classroom_unit_id
  }

  function renderModal() {
    const activities = getClassroomActivities()
    const lessonActivityData = activities.get(lessonActivityId)

    if (!showModal || !(Object.keys(lessonActivityData).length)) { return }

    return (
      <PreviewOrLaunchModal
        classroomUnitId={getClassroomUnitId(lessonActivityData)}
        closeModal={handleToggleModal}
        completed={lessonActivityData.completed}
        lessonID={lessonActivityId}
      />
    );
  }

  function renderActivityCell(lessonActivity, id) {
    const { name, completed, started } = lessonActivity
    const href = `/teachers/classroom_units/${getClassroomUnitId(lessonActivity)}/mark_lesson_as_completed/${id}`;
    return(
      <div className="activity-cell-container">
        <DataTableChip color="green" label={name} onClick={(e) => handleDataTableChipClick(e, id)} />
        {completed && <div className="lesson-complete"><p>Lesson Complete</p><img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/check-circle.svg`} /></div>}
        {started && <a className={sharedButtonClassName} href={href}>Mark As Complete</a>}
      </div>
    )
  }

  function handleLaunchLessonWithNoStudents(e, activityId, classroomUnitId) {
    if (window.confirm("You have no students in this class. Quill Lessons is a collaborative tool for teachers and students to work together. If you'd like to launch this lesson anyway, click OK below. Otherwise, click Cancel.")) {
      window.location.href = `${window.postbuildEnv.DEFAULT_URL}/teachers/classroom_units/${classroomUnitId}/launch_lesson/${activityId}`;
    }
  }

  function renderLaunchLessonCell(lessonActivity) {
    const { completed, studentCount, started } = lessonActivity
    const activityId = getActivityId(lessonActivity)
    const classroomUnitId = getClassroomUnitId(lessonActivity)
    if (completed) {
      return <a className={sharedButtonClassName} href={`/teachers/progress_reports/report_from_classroom_unit_and_activity/${classroomUnitId}/a/${activityId}`} rel="noopener noreferrer" target="_blank">View Report</a>;
    }
    if (studentCount === 0) {
      return <a className={sharedButtonClassName} id="launch-lesson" onClick={(e) => handleLaunchLessonWithNoStudents(e, activityId, classroomUnitId)}>{started ? 'Resume Lesson' : 'Launch Lesson'}</a>;
    }
    if (started) {
      return <a className={sharedButtonClassName} href={`${window.postbuildEnv.DEFAULT_URL}/teachers/classroom_units/${classroomUnitId}/launch_lesson/${activityId}`}>Resume Lesson</a>;
    }
    return <a className={sharedButtonClassName} href={`${window.postbuildEnv.DEFAULT_URL}/teachers/classroom_units/${classroomUnitId}/launch_lesson/${activityId}`}>Launch Lesson</a>;
  }

  function renderCustomizeCell(id) {
    return(
      <a aria-label="Customize lesson" className="customize-link focus-on-light" href={`/customize/${id}`} rel="noopener noreferrer" target="_blank">
        <img alt="extra small pencil icon" src={`${EXTRA_SMALL_ICON_BASE_SRC}/pencil.svg`} />
      </a>
    )
  }

  function renderDownloadCell(id) {
    return(
      <a aria-label="Download lesson" className="download-link focus-on-light" href={`/activities/${id}/supporting_info`} rel="noopener noreferrer" target="_blank">
        <img alt="extra small arrow down icon" src={`${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`} />
      </a>
    )
  }

  function renderClassroomActivities() {
    const lessonsIcon = <img alt="lessons icon" className="lessons-icon" src={`${EXTRA_SMALL_ICON_BASE_SRC}/tool-lessons.svg`} />
    // this data is passed as a Map type so we need to use forEach to iterate through it and push the relevant data into an empty array to pass as the DataTable rows prop
    const rows = [];
    const activities = getClassroomActivities()
    activities.forEach((lessonActivity) => {
      const id = getActivityId(lessonActivity)
      rows.push({
        id,
        tool: lessonsIcon,
        activity: renderActivityCell(lessonActivity, id),
        launchLesson: renderLaunchLessonCell(lessonActivity),
        customize: renderCustomizeCell(id),
        download: renderDownloadCell(id)
      })
    })
    return (
      <DataTable
        className="lesson-activities-table reporting-format"
        headers={headers}
        rows={rows}
      />
    )
  };

  const id = unitId || unit?.id
  
  return (
    <section aria-labelledby={`${id}Name`} className="lesson-unit-container">
      {renderModal()}
      <div className="unit-header-container" id={id}>
        <span className="unit-name">
          <span className="h-pointer" id={`${id}Name`}>{unitName || name}</span>
        </span>
        <div className="assigned-student-info">
          {lockedInfo()}
          {assignedToText()}
        </div>
      </div>
      <div className="table assigned-activities">
        {renderClassroomActivities()}
      </div>
    </section>
  );
}

export default Unit
